import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, posApi } from '@/services/api';
import { createSupplierRouted } from '@/offline';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { isCompanyAdmin } from '@/types';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { Truck, Plus, Search, X, AlertCircle, Menu, FileText, User, Activity, Package, Building2, Phone, Mail, MapPin, Upload } from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import type { POSSupplier } from '@/types';
import {
  validateSupplierForm,
  normalizeSupplierMobile,
  getApiErrorMessage,
  type SupplierFormErrors,
} from '@/utils/supplierValidation';
import { sanitizePhoneInput } from '@/utils/mobileValidation';
import {
  COUNTRIES,
  getCountry,
  getRegionsForCountry,
  isPakistan,
} from '@/data/locales';
import UkPostcodeLookup from '@/components/common/UkPostcodeLookup';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import toast from 'react-hot-toast';
import AddPurchaseInvoiceModal from '../components/suppliers/AddPurchaseInvoiceModal';
import PurchaseInvoiceLedgerModal from '../components/suppliers/PurchaseInvoiceLedgerModal';
import AddSupplierPaymentModal from '../components/suppliers/AddSupplierPaymentModal';
import ContactCard from '@/components/common/ContactCard';
import PartyLedgerModal from '@/components/PartyLedgerModal';
import SupplierLedgerModal from '@/components/SupplierLedgerModal';
import EntityImportWizard from '@/components/dataImport/EntityImportWizard';
import { supplierImportConfig } from '@/components/dataImport/entities/supplierImportConfig';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

interface CityOption {
  name: string;
  city: string;
}

export default function SuppliersPage() {
  const { t } = useTranslation();
  const { role } = useAuthStore();
  const isAdmin = isCompanyAdmin(role);
  const { toggleSidebar } = useUIStore();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [showInvoiceLedger, setShowInvoiceLedger] = useState(false);
  const [showPartyLedger, setShowPartyLedger] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  useEscapeToClose(() => setShowModal(false), showModal);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [selectedSupplierName, setSelectedSupplierName] = useState<string>('');
  const [selectedSupplierObj, setSelectedSupplierObj] = useState<any>(null);
  const [editingSupplier, setEditingSupplier] = useState<POSSupplier | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    label: string;
    type: 'customer' | 'supplier' | 'product' | 'variant';
    errorMessage?: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    supplier_name: '', contact_person: '', mobile: '', email: '', city: '', province: '', postal_code: '',
    country: 'PK', address: '', is_active: true,
    opening_balance: '',
  });
  const [postcodeError, setPostcodeError] = useState('');
  const [errors, setErrors] = useState<SupplierFormErrors>({});
  const [formError, setFormError] = useState('');
  const qc = useQueryClient();

  useEffect(() => {
    setPage(0);
  }, [search]);

  const { data: suppliersData, isLoading } = useQuery({
    queryKey: ['suppliers', search, page],
    queryFn: async () => {
      const res = await posApi.getSuppliers({
        search,
        start: page * LIST_PAGE_SIZE,
        limit: LIST_PAGE_SIZE,
      });
      const message = res.data.message;
      return {
        suppliers: message.suppliers as POSSupplier[],
        total: (message.total as number) || 0,
      };
    },
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => { const res = await authApi.getCompanyProfile(); return res.data.message; },
  });
  const defaultCountry = profile?.country || 'GB';

  const { data: cities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const res = await posApi.getCities();
      return res.data.message as CityOption[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: unknown) => {
      const payload = data as Record<string, unknown>;
      return createSupplierRouted(
        editingSupplier ? { ...payload, name: editingSupplier.name } : payload,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      setShowModal(false);
      resetForm();
      toast.success('Supplier saved successfully');
    },
    onError: (err: unknown) => setFormError(getApiErrorMessage(err, 'Failed to save supplier')),
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => posApi.deleteSupplier(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Supplier removed');
      setDeleteConfirm(null);
    },
    onError: (err: any) => {
      const errMsg = getApiErrorMessage(err, 'Failed to delete supplier');
      setDeleteConfirm((prev) => prev ? { ...prev, errorMessage: errMsg } : null);
    },
  });

  const handleDeleteSupplier = (id: string, label: string, hasLinks: boolean) => {
    if (hasLinks) {
      setDeleteConfirm({
        id,
        label,
        type: 'supplier',
        errorMessage: `This supplier cannot be deleted because there is linked purchase or ledger history associated with this supplier.`
      });
    } else {
      setDeleteConfirm({ id, label, type: 'supplier' });
    }
  };

  const emptyForm = () => ({
    supplier_name: '', contact_person: '', mobile: '', email: '', city: '', province: '', postal_code: '',
    country: defaultCountry, address: '', is_active: true, opening_balance: '',
  });

  const resetForm = () => {
    setFormData(emptyForm());
    setEditingSupplier(null);
    setErrors({});
    setFormError('');
    setPostcodeError('');
  };

  const openEdit = (supplier: POSSupplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplier_name: supplier.supplier_name,
      contact_person: supplier.contact_person || '',
      mobile: supplier.mobile || '',
      email: supplier.email || '',
      city: supplier.city || '',
      province: supplier.province || '',
      postal_code: supplier.postal_code || '',
      country: supplier.country || defaultCountry,
      address: supplier.address || '',
      is_active: supplier.is_active ?? true,
      opening_balance: '',
    });
    setErrors({});
    setFormError('');
    setPostcodeError('');
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateSupplierForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setFormError('');
    const openingBalance = parseFloat(formData.opening_balance) || 0;

    const { opening_balance: _openingRaw, ...formFields } = formData;
    const payload: Record<string, unknown> = {
      ...formFields,
      mobile: formData.mobile.trim() ? normalizeSupplierMobile(formData.mobile, formData.country) : '',
      email: formData.email.trim(),
    };
    if ((!editingSupplier || editingSupplier.can_set_opening) && openingBalance != 0) {
      payload.opening_balance = openingBalance;
    }

    saveMutation.mutate(payload);
  };

  const handleExportData = async () => {
    import('react-hot-toast').then(({ default: toast }) => {
      const toastId = toast.loading('Preparing backup data...');
      posApi.exportSuppliersData()
        .then(res => {
          const blob = new Blob([res.data], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          
          const contentDisposition = res.headers['content-disposition'];
          let filename = 'Suppliers_Export.csv';
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              filename = match[1];
            }
          }
          
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success('Backup downloaded successfully', { id: toastId });
        })
        .catch(err => {
          console.error(err);
          toast.error('Failed to download backup', { id: toastId });
        });
    });
  };

  const suppliers = suppliersData?.suppliers || [];
  const suppliersTotal = suppliersData?.total || 0;
  const hasErrors = Object.keys(errors).length > 0;

  const getCityLabel = (cityLink?: string, country?: string) => {
    if (!cityLink) return '';
    if (!getCountry(country).useCityMaster) return cityLink;
    return cities.find((c) => c.name === cityLink)?.city || cityLink;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <TrialBanner />
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('suppliers.title')}</h1>
                <p className="text-gray-500 text-sm">{t('suppliers.subtitle')}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowLedger(true)}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm text-sm"
              >
                <FileText className="w-4 h-4" /> {t('suppliers.purchaseRegister')}
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleExportData}
                  className="btn-secondary flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" /> Backup Data
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowImportWizard(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> {t('common.import')}
              </button>
              <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" /> {t('suppliers.add')}
              </button>
            </div>
          </div>

          <div className="card p-4 mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.searchNameMobile')}
                className="input-field pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="card p-8 text-center text-gray-400">{t('suppliers.loading')}</div>
          ) : suppliers.length === 0 ? (
            <div className="card p-8 text-center text-gray-400">
              <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('suppliers.empty')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
                {suppliers.map((supplier) => {
                  const totalPurchase = supplier.total_purchase || 0;
                  const paidAmount = supplier.paid_amount || 0;
                  const outstandingAmount = Math.max(0, supplier.outstanding_amount || 0);
                  const advanceAmount = Math.max(0, (supplier as any).advance_amount || 0);

                  const progressPercentage = totalPurchase ? Math.round((paidAmount / totalPurchase) * 100) : 0;
                  const addressText = supplier.address ? `${supplier.address}${supplier.city ? `, ${getCityLabel(supplier.city, supplier.country)}` : ''}` : (getCityLabel(supplier.city, supplier.country) || '');

                  return (
                    <ContactCard
                      key={supplier.name}
                      name={supplier.supplier_name}
                      onEdit={() => openEdit(supplier)}
                      onDelete={isAdmin ? () => handleDeleteSupplier(supplier.name, supplier.supplier_name, (supplier.total_purchase || 0) > 0 || outstandingAmount !== 0 || advanceAmount !== 0) : undefined}
                      onLedger={() => {
                        setSelectedSupplier(supplier.name);
                        setSelectedSupplierName(supplier.supplier_name);
                        setShowPartyLedger(true);
                      }}
                      stats={[
                        {
                          value: totalPurchase,
                          label: t('suppliers.totalPurchase'),
                          icon: Package,
                          iconColorClass: 'text-lime-600',
                          bgClass: 'bg-lime-50',
                        },
                        {
                          value: paidAmount,
                          label: t('suppliers.paid'),
                          icon: PakRupeeIcon,
                          iconColorClass: 'text-gray-400',
                          bgClass: 'bg-gray-50',
                        },
                        {
                          value: advanceAmount,
                          label: t('suppliers.advance'),
                          icon: PakRupeeIcon,
                          iconColorClass: 'text-emerald-600',
                          bgClass: 'bg-emerald-50',
                        },
                        {
                          value: outstandingAmount,
                          label: t('suppliers.outstanding'),
                          icon: PakRupeeIcon,
                          iconColorClass: 'text-red-600',
                          bgClass: 'bg-red-50',
                        },
                      ]}
                      progressLabel={t('suppliers.paidVsOutstanding')}
                      progressPercentage={progressPercentage}
                      phone={supplier.mobile}
                      email={supplier.email}
                      address={addressText}
                      actions={[
                        {
                          label: 'Register',
                          icon: FileText,
                          onClick: () => { setSelectedSupplier(supplier.name); setShowLedger(true); },
                          textClass: 'text-primary-700',
                          bgClass: 'bg-primary-50',
                          hoverClass: 'hover:bg-primary-100'
                        },
                        {
                          label: t('suppliers.addInvoice'),
                          icon: Plus,
                          onClick: () => { setSelectedSupplier(supplier.name); setShowAddInvoice(true); },
                          textClass: 'text-emerald-700',
                          bgClass: 'bg-emerald-50',
                          hoverClass: 'hover:bg-emerald-100'
                        },
                        {
                          label: t('suppliers.addPayment'),
                          icon: Activity,
                          onClick: () => { setSelectedSupplierObj(supplier); setShowAddPayment(true); },
                          textClass: 'text-indigo-700',
                          bgClass: 'bg-indigo-50',
                          hoverClass: 'hover:bg-indigo-100'
                        },
                        {
                          label: t('suppliers.viewInvoices'),
                          icon: FileText,
                          onClick: () => { setSelectedSupplier(supplier.name); setShowInvoiceLedger(true); },
                          textClass: 'text-blue-700',
                          bgClass: 'bg-blue-50',
                          hoverClass: 'hover:bg-blue-100'
                        }
                      ]}
                    />
                  );
                })}
              </div>
              <div className="card overflow-hidden mt-4">
                <Pagination
                  page={page}
                  pageSize={LIST_PAGE_SIZE}
                  total={suppliersTotal}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[95vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
              <h3 className="font-semibold text-gray-900">{editingSupplier ? t('suppliers.edit') : t('suppliers.add')}</h3>
              <button onClick={() => { setShowModal(false); qc.invalidateQueries({ queryKey: ['suppliers'] }); }} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {hasErrors && (
              <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 shrink-0">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <ul className="text-sm text-red-600 space-y-0.5">
                  {Object.values(errors).map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
            {formError && (
              <div className="mx-5 mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 shrink-0">
                {formError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.companyName')} *</label>
                <div className="relative">
                  <Building2 className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    value={formData.supplier_name}
                    onChange={e => setFormData({ ...formData, supplier_name: e.target.value })}
                    className={`input-field pl-10 ${errors.supplier_name ? 'border-red-300 focus:ring-red-500' : ''}`}
                    placeholder="e.g. Ali & Sons #1"
                  />
                </div>
                {errors.supplier_name && (
                  <p className="text-xs text-red-500 mt-1">{errors.supplier_name}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('suppliers.contactPerson')}</label>
                  <div className="relative">
                    <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={formData.contact_person}
                      onChange={e => setFormData({ ...formData, contact_person: e.target.value })}
                      className="input-field pl-10"
                      placeholder="Ahmad"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.mobile')}</label>
                  <div className="relative">
                    <Phone className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={isPakistan(formData.country) ? 11 : 15}
                      value={formData.mobile}
                      onChange={e => setFormData({ ...formData, mobile: sanitizePhoneInput(e.target.value, formData.country) })}
                      className={`input-field pl-10 ${errors.mobile ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder={isPakistan(formData.country) ? '03001234567' : t('customers.phonePlaceholder')}
                    />
                  </div>
                  {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className={`input-field pl-10 ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="example@gmail.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.country')}</label>
                  <select
                    className="input-field"
                    value={formData.country}
                    onChange={(e) => {
                      const country = e.target.value;
                      setFormData({
                        ...formData,
                        country,
                        province: '',
                        city: '',
                        postal_code: '',
                      });
                      setPostcodeError('');
                    }}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.region')}</label>
                  <select
                    className="input-field"
                    value={formData.province}
                    onChange={e => setFormData({ ...formData, province: e.target.value })}
                  >
                    <option value="">{t('common.select')}</option>
                    {getRegionsForCountry(formData.country).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.country === 'US' ? t('common.zipCode') : t('common.postalCode')}
                  </label>
                  {getCountry(formData.country).usePostcodeLookup ? (
                    <UkPostcodeLookup
                      postalCode={formData.postal_code}
                      onPostalCodeChange={(v) => {
                        setPostcodeError('');
                        setFormData((prev) => ({ ...prev, postal_code: v }));
                      }}
                      onFill={(fields) => {
                        setPostcodeError('');
                        setFormData((prev) => ({
                          ...prev,
                          postal_code: fields.postal_code,
                          province: fields.province || prev.province,
                          city: fields.city || prev.city,
                          address: fields.address !== undefined ? fields.address : prev.address,
                        }));
                      }}
                    />
                  ) : (
                    <input
                      className="input-field"
                      value={formData.postal_code}
                      onChange={(e) => {
                        setPostcodeError('');
                        setFormData({ ...formData, postal_code: e.target.value });
                      }}
                    />
                  )}
                  {postcodeError && <p className="text-xs text-red-500 mt-1">{postcodeError}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.city')}</label>
                  <div className="relative">
                    <MapPin className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    {getCountry(formData.country).useCityMaster ? (
                      <select
                        value={formData.city}
                        onChange={e => setFormData({ ...formData, city: e.target.value })}
                        className="input-field pl-10 appearance-none bg-transparent"
                      >
                        <option value="">{t('common.select')}</option>
                        {cities.map((c) => (
                          <option key={c.name} value={c.name}>{c.city}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={formData.city}
                        onChange={e => setFormData({ ...formData, city: e.target.value })}
                        className="input-field pl-10"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.address')}</label>
                <textarea
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  className="input-field min-h-[80px] resize-none"
                  placeholder={t('suppliers.fullAddressPlaceholder')}
                />
              </div>
              {(!editingSupplier || editingSupplier.can_set_opening) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input-field"
                    placeholder="+ payable / − advance"
                    value={formData.opening_balance}
                    onChange={e => setFormData({ ...formData, opening_balance: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Positive = you owe supplier. Negative = advance paid to supplier.
                    {editingSupplier ? ' Only available when this supplier has no ledger entries yet.' : ''}
                  </p>
                  {errors.opening_balance && <p className="text-xs text-red-500 mt-1">{errors.opening_balance}</p>}
                </div>
              )}
              </div>

              <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); qc.invalidateQueries({ queryKey: ['suppliers'] }); }}
                  className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="w-full sm:w-auto px-6 py-2.5 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                >
                  {saveMutation.isPending ? t('common.saving') : (editingSupplier ? t('common.saveChanges') : t('suppliers.add'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddInvoice && (
        <AddPurchaseInvoiceModal
          isOpen={showAddInvoice}
          onClose={() => {
            setShowAddInvoice(false);
            qc.invalidateQueries({ queryKey: ['suppliers'] });
          }}
          supplier={selectedSupplier}
        />
      )}

      {showInvoiceLedger && (
        <PurchaseInvoiceLedgerModal
          isOpen={showInvoiceLedger}
          onClose={() => {
            setShowInvoiceLedger(false);
            qc.invalidateQueries({ queryKey: ['suppliers'] });
          }}
          supplier={selectedSupplier}
        />
      )}

      <SupplierLedgerModal
        isOpen={showLedger}
        onClose={() => {
          setShowLedger(false);
          setSelectedSupplier('');
          qc.invalidateQueries({ queryKey: ['suppliers'] });
        }}
        suppliers={suppliers}
        initialSupplier={selectedSupplier}
      />

      <PartyLedgerModal
        isOpen={showPartyLedger}
        onClose={() => {
          setShowPartyLedger(false);
          setSelectedSupplier('');
          setSelectedSupplierName('');
          qc.invalidateQueries({ queryKey: ['suppliers'] });
        }}
        partyType="POS Supplier"
        party={selectedSupplier}
        partyName={selectedSupplierName}
      />

      {showAddPayment && (
        <AddSupplierPaymentModal
          isOpen={showAddPayment}
          onClose={() => {
            setShowAddPayment(false);
            qc.invalidateQueries({ queryKey: ['suppliers'] });
          }}
          supplier={selectedSupplierObj}
        />
      )}

      {showImportWizard && (
        <EntityImportWizard
          config={supplierImportConfig}
          onClose={() => {
            setShowImportWizard(false);
            qc.invalidateQueries({ queryKey: ['suppliers'] });
          }}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['suppliers'] })}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${deleteConfirm.errorMessage ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                <AlertCircle className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-950">
                {deleteConfirm.errorMessage ? t('confirmDelete.cannotDelete') : t('confirmDelete.title')}
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {deleteConfirm.errorMessage || t('confirmDelete.message', { label: deleteConfirm.label })}
            </p>

            <div className="flex justify-end gap-3 pt-2">
              {deleteConfirm.errorMessage ? (
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="btn-primary w-24"
                >
                  {t('confirmDelete.ok')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="btn-secondary"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      deleteMutation.mutate(deleteConfirm.id);
                    }}
                    disabled={deleteMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 rounded-lg transition-colors"
                  >
                    {deleteMutation.isPending ? t('confirmDelete.deleting') : t('common.delete')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <SupplierLedgerModal
        isOpen={showLedger}
        onClose={() => setShowLedger(false)}
        initialSupplier={selectedSupplier}
        suppliers={suppliersData?.suppliers || []}
      />
    </div>
  );
}
